import * as React from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  MenuItem,
  Menu,
  CardHeader,
  DialogActions
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import DashedDivider from "common/ui/DashedDivider";
import {
  useMachineSummary,
  useAddServiceRecord,
  useMachineSearch
} from "services/machines.service";
import { useAppDispatch, useAppSelector } from "context/hooks";
import {
  setMachineSearchService,
  setRequestSoftware
} from "context/modal.context";
import toast from "react-hot-toast";
import queryClient from "helpers/queryClient";
import { selectMachineSearchServiceProps } from "context/modal.selector";
import LoadingButton from "common/ui/LoadingButton";
import "./MachineSearchService.scss";
import Machine from "types/Machine.type";

export default function ServiceRecord() {
  const { t } = useTranslation();
  const props = useAppSelector(selectMachineSearchServiceProps);
  const dispatch = useAppDispatch();

  const searchInput = React.useRef(null);
  const [searchText, setSearchText] = React.useState(null);
  const [machine, setMachine] = React.useState<null | Machine>(null);
  const [anchorE1, setAnchorE1] = React.useState<null | HTMLElement>(null);

  const machines = useMachineSearch({
    pageNumber: 1,
    rowCount: 5,
    searchText
  });
  const machineDetails = useMachineSummary(machine?.id);
  const serviceRecord = useAddServiceRecord(onServiceRecordFetch);
  const showMachineMenu = Boolean(anchorE1);

  async function onServiceRecordFetch(_data, error) {
    await queryClient.invalidateQueries({
      queryKey: ["machine", "detail"],
      exact: false
    });

    if (!error) {
      toast.success(t("machineSearchService.pinGeneratedSuccessfully"));
      dispatch(
        setRequestSoftware({
          open: true,
          machineId: machineDetails?.data?.machineSummary.id
        })
      );
    }
    dismiss();
  }

  function handleContinueButton() {
    if (machineDetails.data?.machineSummary.hasOpenServiceRecord) {
      dispatch(
        setRequestSoftware({
          open: true,
          machineId: machineDetails?.data?.machineSummary.id
        })
      );
      dismiss();
    } else {
      serviceRecord.mutate(machineDetails.data?.machineSummary.id);
    }
  }

  function dismiss() {
    setMachine(null);
    dispatch(setMachineSearchService({ open: false }));
  }

  const handleSearch = (event) => {
    event.preventDefault();

    const text = event.target.elements.search.value;

    if (text && text.length >= 3) {
      setSearchText(text);
      setAnchorE1(searchInput.current);
    }

    if (!Boolean(text)) {
      setSearchText(text);
    }
  };

  const handleReset = () => {
    setMachine(null);
  };

  function handleCloseMachineMenu() {
    setAnchorE1(null);
  }

  const handleMachineSelect = (item) => {
    setMachine(item);
    setAnchorE1(null);
  };

  return (
    <Dialog open={props.open} scroll="body" maxWidth="sm" fullWidth>
      <DialogTitle
        component={CardHeader}
        avatar={<SettingsIcon />}
        title={t("machineSearchService.machineService")}
        titleTypographyProps={{ variant: "h6" }}
        color="primary"
        action={
          <IconButton onClick={dismiss}>
            <CloseIcon />
          </IconButton>
        }
      />
      <DialogContent sx={{ py: 0 }}>
        <DashedDivider />
        <Typography marginY={2} color="grey.600">
          {t("machineSearchService.serviceMessage")}
        </Typography>
        <form onSubmit={handleSearch}>
          {!!machine ? (
            <Box
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
              border="1px solid"
              borderColor="gray.light"
              borderRadius={1}
              p={1}
              mb={18}
            >
              <Typography color="gray.light">
                {`${machine.serialNumber} - (${machine.name})`}
              </Typography>
              <Button onClick={() => handleReset()}>
                {t("machineSearchService.change")}
              </Button>
            </Box>
          ) : (
            <TextField
              id="search"
              fullWidth
              type="search"
              placeholder={t("machineSearchService.searchPlaceHolder")}
              inputRef={searchInput}
              onChange={(event) => {
                if (!Boolean(event.target.value)) {
                  setSearchText(event.target.value);
                }
              }}
              sx={{ height: 200 }}
              InputProps={{
                endAdornment: (
                  <IconButton type="submit">
                    <SearchIcon />
                  </IconButton>
                )
              }}
            />
          )}
        </form>
        {machines.isSuccess ? (
          <Menu
            anchorEl={anchorE1}
            open={showMachineMenu}
            onClose={handleCloseMachineMenu}
            sx={{ overflowY: "auto" }}
          >
            {[...machines.data?.machineDetails].map((item: any) => (
              <MenuItem
                key={item.id}
                onClick={() => handleMachineSelect(item)}
                classes={{
                  root: "machine-search-service-dropdown__search-item"
                }}
              >
                <ListItemText
                  primary={`${item.serialNumber} - (${item.name})`}
                  primaryTypographyProps={{ color: "primary.dark" }}
                />
              </MenuItem>
            ))}

            {machines.data.machineDetails.length === 0 && (
              <MenuItem sx={{ width: 550 }}>
                <ListItemText
                  primary={t("machineSearchService.noMachinesFound")}
                />
              </MenuItem>
            )}
          </Menu>
        ) : null}
        <DashedDivider />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", py: 2 }}>
        <LoadingButton
          loading={machineDetails.isFetching || serviceRecord.isLoading}
          disabled={!machine}
          type="submit"
          variant="outlined"
          onClick={handleContinueButton}
        >
          {t("machineSearchService.continue")}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
